module Main exposing (main)

import Browser
import Browser.Events
import Date
import Dict exposing (Dict)
import Html exposing (..)
import Html.Attributes as HA
import Html.Events as HE
import Http
import Iso8601
import Json.Decode as Decode exposing (Decoder)
import Maybe.Extra as ME
import Svg
import Svg.Attributes as SA
import Svg.Events as SE
import Time



-- MODEL


type RemoteData a
    = Loading
    | Failure String
    | Success a


type alias Match =
    { id : Int
    , home : String
    , away : String
    , startTime : Time.Posix
    , venue : String
    , city : String
    , state : MatchState
    }


type alias Score =
    { home : Int
    , away : Int
    }


type MatchState
    = Upcoming
    | Final Score


type MatchResult
    = Win
    | Lose
    | Tie


type alias Team =
    { id : Int
    , name : String
    , group : String
    , pot : Int
    , code : String
    , fifaCode : String
    }


type alias Group =
    { pot1 : Team
    , pot2 : Team
    , pot3 : Team
    , pot4 : Team
    , match12 : Match
    , match13 : Match
    , match14 : Match
    , match23 : Match
    , match24 : Match
    , match34 : Match
    }


teamsByGroup : List Team -> Dict String (List Team)
teamsByGroup teams =
    List.foldl
        (\team acc ->
            Dict.update team.group
                (Maybe.withDefault [] >> (::) team >> Just)
                acc
        )
        Dict.empty
        teams


findMatch : List Match -> String -> String -> Maybe Match
findMatch matches t1 t2 =
    List.filter
        (\m ->
            (m.home == t1 && m.away == t2)
                || (m.home == t2 && m.away == t1)
        )
        matches
        |> List.head


buildGroup : List Team -> List Match -> Maybe Group
buildGroup teams matches =
    case List.sortBy .pot teams of
        [ p1, p2, p3, p4 ] ->
            Just Group
                |> ME.andMap (Just p1)
                |> ME.andMap (Just p2)
                |> ME.andMap (Just p3)
                |> ME.andMap (Just p4)
                |> ME.andMap (findMatch matches p1.name p2.name)
                |> ME.andMap (findMatch matches p1.name p3.name)
                |> ME.andMap (findMatch matches p1.name p4.name)
                |> ME.andMap (findMatch matches p2.name p3.name)
                |> ME.andMap (findMatch matches p2.name p4.name)
                |> ME.andMap (findMatch matches p3.name p4.name)

        _ ->
            Nothing


type Card
    = MatchCard Int
    | TeamCard Int


type alias TeamCardParams =
    { team : Team
    , group : Group
    }


type alias MatchCardParams =
    { match : Match
    , home : Team
    , away : Team
    , venue : String
    }


teamCardParams : Model -> Int -> Maybe TeamCardParams
teamCardParams model id =
    case ( model.matches, model.teams, model.groups ) of
        ( Success matches, Success teams, Success groups ) ->
            let
                maybeTeam =
                    List.head (List.filter (\team -> team.id == id) teams)

                maybeGroup =
                    Dict.get (Maybe.withDefault "" (Maybe.map .group maybeTeam)) groups
            in
            Just TeamCardParams
                |> ME.andMap maybeTeam
                |> ME.andMap maybeGroup

        _ ->
            Nothing


matchCardParams : Model -> Int -> Maybe MatchCardParams
matchCardParams model id =
    case ( model.matches, model.teams ) of
        ( Success matches, Success teams ) ->
            let
                maybeMatch =
                    List.head (List.filter (\match -> match.id == id) matches)

                maybeHome =
                    Maybe.andThen
                        (\match -> List.head (List.filter (\team -> team.name == match.home) teams))
                        maybeMatch

                maybeAway =
                    Maybe.andThen
                        (\match -> List.head (List.filter (\team -> team.name == match.away) teams))
                        maybeMatch
            in
            Just MatchCardParams
                |> ME.andMap maybeMatch
                |> ME.andMap maybeHome
                |> ME.andMap maybeAway
                |> ME.andMap (Maybe.map .venue maybeMatch)

        _ ->
            Nothing


type alias Model =
    { matches : RemoteData (List Match)
    , teams : RemoteData (List Team)
    , groups : RemoteData (Dict String Group)
    , cardStack : List Card
    , timeZone : String
    }


type alias Flags =
    { timeZone : String }


init : Flags -> ( Model, Cmd Msg )
init flags =
    ( { matches = Loading
      , teams = Loading
      , groups = Loading
      , cardStack = []
      , timeZone = flags.timeZone
      }
    , Cmd.batch
        [ fetchMatches
        , fetchTeams
        ]
    )



-- COMPUTATIONS


groupTeams : Group -> List Team
groupTeams group =
    [ group.pot1, group.pot2, group.pot3, group.pot4 ]


groupMatches : Group -> List Match
groupMatches group =
    [ group.match12, group.match13, group.match14, group.match23, group.match24, group.match34 ]


matchPointsForTeam : Match -> String -> Maybe Int
matchPointsForTeam match teamName =
    let
        pointsFromScore goalsFor goalsAgainst =
            if goalsFor > goalsAgainst then
                3

            else if goalsFor == goalsAgainst then
                1

            else
                0
    in
    case match.state of
        Upcoming ->
            Nothing

        Final score ->
            if match.home == teamName then
                Just (pointsFromScore score.home score.away)

            else if match.away == teamName then
                Just (pointsFromScore score.away score.home)

            else
                Nothing


groupPointsForTeam : Group -> String -> Int
groupPointsForTeam group teamName =
    groupMatches group
        |> List.filterMap (\m -> matchPointsForTeam m teamName)
        |> List.sum


remainingMatchesForTeam : Group -> String -> Int
remainingMatchesForTeam group teamName =
    let
        relevant match =
            List.member teamName [ match.home, match.away ] && match.state == Upcoming
    in
    groupMatches group
        |> List.filter relevant
        |> List.length


finalPointsRangeForTeam : Group -> String -> ( Int, Int )
finalPointsRangeForTeam group teamName =
    let
        currentPoints =
            groupPointsForTeam group teamName
    in
    ( currentPoints, currentPoints + 3 * remainingMatchesForTeam group teamName )


finalPointsAverageForTeam : Group -> String -> Float
finalPointsAverageForTeam group teamName =
    let
        ( min, max ) =
            finalPointsRangeForTeam group teamName
    in
    (toFloat min + toFloat max) / 2


matchResult : Group -> String -> String -> Maybe MatchResult
matchResult group team1 team2 =
    let
        isMatchBetween : Match -> Bool
        isMatchBetween match =
            (match.home == team1 && match.away == team2)
                || (match.home == team2 && match.away == team1)

        resultForTeam1 : Match -> Maybe MatchResult
        resultForTeam1 match =
            case match.state of
                Upcoming ->
                    Nothing

                Final score ->
                    let
                        ( team1Score, team2Score ) =
                            if match.home == team1 then
                                ( score.home, score.away )

                            else
                                ( score.away, score.home )
                    in
                    if team1Score > team2Score then
                        Just Win

                    else if team1Score < team2Score then
                        Just Lose

                    else
                        Just Tie

        allMatches =
            [ group.match12
            , group.match13
            , group.match14
            , group.match23
            , group.match24
            , group.match34
            ]
    in
    allMatches
        |> List.filter isMatchBetween
        |> List.head
        |> Maybe.andThen resultForTeam1



-- UPDATE


type Msg
    = NoOp
    | GotMatches (Result Http.Error (List Match))
    | GotTeams (Result Http.Error (List Team))
    | BuildGroups (List Team) (List Match)
    | OpenCard Card
    | CloseCard


update : Msg -> Model -> ( Model, Cmd Msg )
update msg model =
    case msg of
        NoOp ->
            ( model, Cmd.none )

        GotMatches (Ok matches) ->
            case model.teams of
                Success teams ->
                    update (BuildGroups teams matches) { model | matches = Success matches }

                _ ->
                    ( { model | matches = Success matches }, Cmd.none )

        GotMatches (Err _) ->
            ( { model | matches = Failure "Failed to load matches" }, Cmd.none )

        GotTeams (Ok teams) ->
            case model.matches of
                Success matches ->
                    update (BuildGroups teams matches) { model | teams = Success teams }

                _ ->
                    ( { model | teams = Success teams }, Cmd.none )

        GotTeams (Err _) ->
            ( { model | teams = Failure "Failed to load teams" }, Cmd.none )

        BuildGroups teams matches ->
            let
                maybeGroups =
                    teamsByGroup teams
                        |> Dict.toList
                        |> ME.traverse
                            (\( g, ts ) ->
                                buildGroup ts matches
                                    |> Maybe.map (Tuple.pair g)
                            )
                        |> Maybe.map Dict.fromList
            in
            case maybeGroups of
                Just groups ->
                    ( { model | groups = Success groups }, Cmd.none )

                Nothing ->
                    ( { model | groups = Failure "Failed to build groups" }, Cmd.none )

        OpenCard card ->
            ( { model | cardStack = card :: model.cardStack }, Cmd.none )

        CloseCard ->
            ( { model | cardStack = List.drop 1 model.cardStack }, Cmd.none )



-- HTTP


fetchMatches : Cmd Msg
fetchMatches =
    Http.get
        { url = "/api/matches"
        , expect = Http.expectJson GotMatches (Decode.list matchDecoder)
        }


fetchTeams : Cmd Msg
fetchTeams =
    Http.get
        { url = "/api/teams"
        , expect = Http.expectJson GotTeams (Decode.list teamDecoder)
        }



-- DECODERS


matchStateDecoder : Decoder MatchState
matchStateDecoder =
    Decode.map2 Tuple.pair
        (Decode.field "home_score" (Decode.nullable Decode.int))
        (Decode.field "away_score" (Decode.nullable Decode.int))
        |> Decode.andThen
            (\( homeScore, awayScore ) ->
                case ( homeScore, awayScore ) of
                    ( Just home, Just away ) ->
                        Decode.succeed <|
                            Final
                                { home = home
                                , away = away
                                }

                    _ ->
                        Decode.succeed Upcoming
            )


matchDecoder : Decoder Match
matchDecoder =
    Decode.map7 Match
        (Decode.field "id" Decode.int)
        (Decode.field "home" Decode.string)
        (Decode.field "away" Decode.string)
        (Decode.field "start_time" Decode.int |> Decode.map (\s -> Time.millisToPosix (s * 1000)))
        (Decode.field "venue" Decode.string)
        (Decode.field "city" Decode.string)
        matchStateDecoder


teamDecoder : Decoder Team
teamDecoder =
    Decode.map6 Team
        (Decode.field "id" Decode.int)
        (Decode.field "name" Decode.string)
        (Decode.field "group" Decode.string)
        (Decode.field "pot" Decode.int)
        (Decode.field "code" (Decode.oneOf [ Decode.string, Decode.null "xx" ]))
        (Decode.field "fifa_code" (Decode.oneOf [ Decode.string, Decode.null "XXX" ]))



-- VIEW


view : Model -> Html Msg
view model =
    let
        cardContent =
            case List.head model.cardStack of
                Just card ->
                    viewCard model card

                Nothing ->
                    text ""
    in
    div [ HA.class "p-8" ]
        [ viewHeader
        , cardContent
        , groupsSection model
        ]


monthDay : String -> Time.Posix -> Html Msg
monthDay timeZone time =
    node "relative-time"
        [ HA.attribute "datetime" (Iso8601.fromTime time)
        , HA.attribute "format" "datetime"
        , HA.attribute "weekday" ""
        , HA.attribute "month" "numeric"
        , HA.attribute "day" "numeric"
        , HA.attribute "time-zone" timeZone
        , HA.attribute "no-title" ""
        ]
        [ text "" ]


timeWithZone : String -> Time.Posix -> Html Msg
timeWithZone timeZone time =
    node "relative-time"
        [ HA.attribute "datetime" (Iso8601.fromTime time)
        , HA.attribute "format" "datetime"
        , HA.attribute "hour" "numeric"
        , HA.attribute "minute" "2-digit"
        , HA.attribute "time-zone-name" "short"
        , HA.attribute "time-zone" timeZone
        , HA.attribute "no-title" ""
        ]
        [ text "" ]


viewHeader : Html Msg
viewHeader =
    div [ HA.class "max-w-7xl mx-auto px-6 pt-10 pb-6 text-center" ]
        [ p
            [ HA.class "text-[11px] md:text-xs uppercase tracking-[0.24em] text-zinc-500 font-medium" ]
            [ text "2026 Tournament Explorer" ]
        , h1
            [ HA.class "mt-4 text-4xl md:text-5xl font-semibold tracking-tight text-zinc-950 font-display" ]
            [ text "World Cup Atlas" ]
        , div [ HA.class "mt-8 flex items-center justify-center gap-6" ]
            [ div [ HA.class "hidden sm:block h-px w-28 md:w-44 bg-zinc-200" ] []
            , h2
                [ HA.class "text-lg md:text-xl font-medium tracking-tight text-zinc-600" ]
                [ text "Group Stage" ]
            , div [ HA.class "hidden sm:block h-px w-28 md:w-44 bg-zinc-200" ] []
            ]
        ]


viewCard : Model -> Card -> Html Msg
viewCard model card =
    let
        content =
            case card of
                MatchCard id ->
                    case matchCardParams model id of
                        Just params ->
                            viewMatchCard model.timeZone params

                        Nothing ->
                            viewErrorCard

                TeamCard id ->
                    case teamCardParams model id of
                        Just params ->
                            viewTeamCard params

                        Nothing ->
                            viewErrorCard
    in
    div
        [ HA.class "fixed inset-0 flex items-center justify-center"
        , HE.onClick CloseCard
        ]
        [ div
            [ HA.class "relative w-xl max-w-[90vw] bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 transition-all duration-300 ease-in-out p-6 "
            , HE.stopPropagationOn "click" (Decode.succeed ( NoOp, True ))
            ]
            [ button
                [ HA.class "absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl leading-none"
                , HE.onClick CloseCard
                ]
                [ text "×" ]
            , content
            ]
        ]


formatTime : Time.Zone -> Time.Posix -> String
formatTime zone posix =
    let
        hour24 =
            Time.toHour zone posix

        minute =
            Time.toMinute zone posix

        hour12 =
            if modBy 12 hour24 == 0 then
                12

            else
                modBy 12 hour24

        ampm =
            if hour24 < 12 then
                "AM"

            else
                "PM"

        minuteStr =
            String.padLeft 2 '0' (String.fromInt minute)
    in
    String.fromInt hour12 ++ ":" ++ minuteStr ++ " " ++ ampm


viewMatchCard : String -> MatchCardParams -> Html Msg
viewMatchCard timeZone params =
    div
        [ HA.class "space-y-6" ]
        [ -- MATCHUP
          div
            [ HA.class "flex items-center justify-between text-center" ]
            [ -- Home
              div [ HA.class "flex flex-col items-center gap-2 w-1/3" ]
                [ img
                    [ HA.src ("/badges/" ++ params.home.code ++ ".svg")
                    , HA.class "w-16 h-16 object-contain border border-black rounded-full"
                    ]
                    []
                , div [ HA.class "font-medium" ]
                    [ text params.home.name ]
                ]

            -- VS
            , div [ HA.class "text-gray-400 text-sm font-semibold w-1/3" ]
                [ text "vs" ]

            -- Away
            , div [ HA.class "flex flex-col items-center gap-2 w-1/3" ]
                [ img
                    [ HA.src ("/badges/" ++ params.away.code ++ ".svg")
                    , HA.class "w-16 h-16 object-contain border border-black rounded-full"
                    ]
                    []
                , div [ HA.class "font-medium" ]
                    [ text params.away.name ]
                ]
            ]

        -- DATE / TIME / VENUE
        , div
            [ HA.class "text-sm text-gray-500 text-center space-y-1" ]
            [ div [] [ timeWithZone timeZone params.match.startTime ]
            , div [] [ text params.venue ]
            ]
        ]


viewTeamCard : TeamCardParams -> Html Msg
viewTeamCard params =
    let
        rangeToString ( i, j ) =
            String.fromInt i ++ " to " ++ String.fromInt j

        standingsRow teamName =
            div
                [ HA.class "grid grid-cols-4 gap-2 text-sm py-1 border-b border-gray-100 last:border-0" ]
                [ div [ HA.class "font-medium truncate" ] [ text teamName ]
                , div [ HA.class "text-gray-600 text-right" ]
                    [ text (String.fromInt (groupPointsForTeam params.group teamName)) ]
                , div [ HA.class "text-gray-500 text-right" ]
                    [ text (String.fromInt (remainingMatchesForTeam params.group teamName)) ]
                , div [ HA.class "text-gray-500 text-right" ]
                    [ text (rangeToString (finalPointsRangeForTeam params.group teamName)) ]
                ]
    in
    div
        [ HA.class "space-y-4" ]
        [ -- Header
          div [ HA.class "flex items-center gap-4" ]
            [ img
                [ HA.src ("/badges/" ++ params.team.code ++ ".svg")
                , HA.class "w-12 h-12 object-contain border border-black rounded-full"
                ]
                []
            , div []
                [ div [ HA.class "text-lg font-semibold" ] [ text params.team.name ]
                , div [ HA.class "text-sm text-gray-500" ]
                    [ text ("Group " ++ params.team.group) ]
                ]
            ]

        -- Section title
        , div [ HA.class "text-sm font-semibold text-gray-700 uppercase tracking-wide" ]
            [ text "Group standings" ]

        -- Column headers
        , div
            [ HA.class "grid grid-cols-4 gap-2 text-xs text-gray-400 uppercase" ]
            [ div [] [ text "Team" ]
            , div [ HA.class "text-right" ] [ text "Points" ]
            , div [ HA.class "text-right" ] [ text "Games Left" ]
            , div [ HA.class "text-right" ] [ text "Final Point Range" ]
            ]

        -- Rows
        , div []
            (List.map standingsRow
                (List.sortBy
                    (\teamName -> -(finalPointsAverageForTeam params.group teamName))
                    (List.map .name (groupTeams params.group))
                )
            )
        ]


viewErrorCard : Html Msg
viewErrorCard =
    text "card could not be loaded"


groupsSection : Model -> Html Msg
groupsSection model =
    div []
        [ groupsContent model ]


groupsContent : Model -> Html Msg
groupsContent model =
    case model.groups of
        Failure err ->
            div [ HA.class "text-red-500" ] [ text err ]

        Success groups ->
            viewGroups model.timeZone groups

        _ ->
            div [ HA.class "flex items-center gap-2 text-gray-500" ]
                [ div [ HA.class "animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-blue-600" ] []
                , text "Loading..."
                ]


viewGroups : String -> Dict String Group -> Html Msg
viewGroups timeZone groups =
    div [ HA.class "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" ]
        (groups
            |> Dict.toList
            |> List.map (\( g, group ) -> viewGroup timeZone g group)
        )


viewGroup : String -> String -> Group -> Html Msg
viewGroup timeZone groupName group =
    div
        [ HA.class "bg-white rounded-lg p-4 shadow-sm"
        , HA.style "background-color" "#f8f7f7"
        ]
        [ h3 [ HA.class "font-bold text-lg mb-2 uppercase" ] [ text <| "Group " ++ groupName ]
        , viewGroupGraphic timeZone group
        ]


type alias CircleParams =
    { centerX : Float
    , centerY : Float
    , radius : Float
    }


type alias CircleStyle =
    { stroke : String
    , strokeWidth : String
    , fill : String
    }


defaultCircleStyle : CircleStyle
defaultCircleStyle =
    { stroke = "#464a5a"
    , strokeWidth = "2"
    , fill = "none"
    }


{-| turn by -1/4 so that the origin is on the top of the circle
-}
circleOnCircle : CircleParams -> Float -> Float -> CircleParams
circleOnCircle base turn radius =
    { centerX = base.centerX + base.radius * cos (turns (turn - 1 / 4))
    , centerY = base.centerY + base.radius * sin (turns (turn - 1 / 4))
    , radius = radius
    }


drawCircle : CircleParams -> CircleStyle -> Html msg
drawCircle params style =
    Svg.circle
        [ SA.cx (String.fromFloat params.centerX)
        , SA.cy (String.fromFloat params.centerY)
        , SA.r (String.fromFloat params.radius)
        , SA.fill style.fill
        , SA.stroke style.stroke
        , SA.strokeWidth style.strokeWidth
        ]
        []


drawCountryCircle : String -> CircleParams -> Html msg
drawCountryCircle code params =
    let
        imgSize =
            params.radius * 2
    in
    Svg.g
        [ SA.class "cursor-pointer transition ease-in-out hover:scale-110 origin-center [transform-box:fill-box]" ]
        [ Svg.image
            [ SA.xlinkHref ("/badges/" ++ code ++ ".svg")
            , SA.x (String.fromFloat (params.centerX - params.radius))
            , SA.y (String.fromFloat (params.centerY - params.radius))
            , SA.width (String.fromFloat imgSize)
            ]
            []
        , drawCircle params defaultCircleStyle
        ]


drawDateCircle : Html Msg -> CircleParams -> CircleStyle -> Html Msg
drawDateCircle date params style =
    Svg.g
        [ SA.class "cursor-pointer transition ease-in-out hover:scale-110 origin-center [transform-box:fill-box]" ]
        [ Svg.circle
            [ SA.cx (String.fromFloat params.centerX)
            , SA.cy (String.fromFloat params.centerY)
            , SA.r (String.fromFloat params.radius)
            , SA.fill style.fill
            , SA.stroke style.stroke
            , SA.strokeWidth style.strokeWidth
            ]
            []
        , Svg.foreignObject
            [ SA.x (String.fromFloat (params.centerX - params.radius))
            , SA.y (String.fromFloat (params.centerY - params.radius / 2))
            , SA.width (String.fromFloat (params.radius * 2))
            , SA.height (String.fromFloat params.radius)
            ]
            [ Html.div
                [ HA.style "display" "flex"
                , HA.style "align-items" "center"
                , HA.style "justify-content" "center"
                , HA.style "width" "100%"
                , HA.style "height" "100%"
                , HA.style "font-size" (String.fromFloat params.radius ++ "px")
                , HA.style "color" style.stroke
                , HA.style "text-align" "center"
                , HA.style "line-height" "1"
                ]
                [ date ]
            ]
        ]


drawLine : ( Float, Float ) -> Float -> Float -> Html msg
drawLine ( x1, y1 ) turn distance =
    let
        point =
            circleOnCircle
                (CircleParams x1 y1 distance)
                turn
                0
    in
    Svg.line
        [ SA.x1 (String.fromFloat x1)
        , SA.y1 (String.fromFloat y1)
        , SA.x2 (String.fromFloat point.centerX)
        , SA.y2 (String.fromFloat point.centerY)
        , SA.stroke "#464a5a"
        , SA.strokeWidth "2"
        ]
        []


displacementTurns : Float -> Float -> Float
displacementTurns radius distance =
    let
        ratio =
            distance / (2 * radius)
    in
    radiansToTurns (2 * asin ratio)


radiansToTurns : Float -> Float
radiansToTurns radians =
    radians / (2 * pi)


viewGroupGraphic : String -> Group -> Html Msg
viewGroupGraphic timeZone group =
    let
        teamRadius =
            50

        matchRadius =
            35

        resultRadius =
            10

        padding =
            10

        baseRadius =
            300

        zeroCircle =
            CircleParams 400 400 0

        baseCircle =
            circleOnCircle zeroCircle 0 baseRadius

        innerCircle =
            circleOnCircle zeroCircle 0 (teamRadius + padding + resultRadius)

        middleCircle =
            circleOnCircle zeroCircle 0 (baseRadius / 2)

        outerCircle =
            circleOnCircle zeroCircle 0 (baseRadius - teamRadius - padding - resultRadius)

        displacement =
            displacementTurns baseRadius (teamRadius + padding + resultRadius)

        bgColor =
            "#f8f7f7"

        maskStyle =
            { defaultCircleStyle | fill = bgColor, strokeWidth = "0" }

        filledStyle =
            { defaultCircleStyle | fill = bgColor }

        filledByResult maybeResult =
            let
                color =
                    case maybeResult of
                        Just result ->
                            case result of
                                Win ->
                                    "#2ea92e"

                                Lose ->
                                    "#d52626"

                                Tie ->
                                    "#ffdd41"

                        Nothing ->
                            bgColor
            in
            { defaultCircleStyle | fill = color }
    in
    Svg.svg
        [ SA.viewBox "0 0 800 800"
        ]
        [ -- Outer ring
          drawCircle baseCircle defaultCircleStyle

        -- Spokes
        , drawLine ( zeroCircle.centerX, zeroCircle.centerY ) 0 baseRadius
        , drawLine ( zeroCircle.centerX, zeroCircle.centerY ) (1 / 3) baseRadius
        , drawLine ( zeroCircle.centerX, zeroCircle.centerY ) (2 / 3) baseRadius

        -- Teams
        , Svg.g [ SE.onClick (OpenCard (TeamCard group.pot1.id)) ]
            [ drawCircle (circleOnCircle baseCircle 0 (teamRadius + padding)) maskStyle
            , drawCountryCircle group.pot1.code (circleOnCircle baseCircle 0 teamRadius)
            ]
        , Svg.g [ SE.onClick (OpenCard (TeamCard group.pot2.id)) ]
            [ drawCircle (circleOnCircle zeroCircle 0 (teamRadius + padding)) maskStyle
            , drawCountryCircle group.pot2.code (circleOnCircle zeroCircle 0 teamRadius)
            ]
        , Svg.g [ SE.onClick (OpenCard (TeamCard group.pot3.id)) ]
            [ drawCircle (circleOnCircle baseCircle (2 / 3) (teamRadius + padding)) maskStyle
            , drawCountryCircle group.pot3.code (circleOnCircle baseCircle (2 / 3) teamRadius)
            ]
        , Svg.g [ SE.onClick (OpenCard (TeamCard group.pot4.id)) ]
            [ drawCircle (circleOnCircle baseCircle (1 / 3) (teamRadius + padding)) maskStyle
            , drawCountryCircle group.pot4.code (circleOnCircle baseCircle (1 / 3) teamRadius)
            ]

        -- Matches
        , Svg.g
            [ SE.onClick (OpenCard (MatchCard group.match12.id)) ]
            [ drawDateCircle (monthDay timeZone group.match12.startTime) (circleOnCircle middleCircle 0 matchRadius) maskStyle ]
        , Svg.g
            [ SE.onClick (OpenCard (MatchCard group.match13.id)) ]
            [ drawDateCircle (monthDay timeZone group.match13.startTime) (circleOnCircle baseCircle (5 / 6) matchRadius) maskStyle ]
        , Svg.g
            [ SE.onClick (OpenCard (MatchCard group.match14.id)) ]
            [ drawDateCircle (monthDay timeZone group.match14.startTime) (circleOnCircle baseCircle (1 / 6) matchRadius) maskStyle ]
        , Svg.g
            [ SE.onClick (OpenCard (MatchCard group.match23.id)) ]
            [ drawDateCircle (monthDay timeZone group.match23.startTime) (circleOnCircle middleCircle (2 / 3) matchRadius) maskStyle ]
        , Svg.g
            [ SE.onClick (OpenCard (MatchCard group.match24.id)) ]
            [ drawDateCircle (monthDay timeZone group.match24.startTime) (circleOnCircle middleCircle (1 / 3) matchRadius) maskStyle ]
        , Svg.g
            [ SE.onClick (OpenCard (MatchCard group.match34.id)) ]
            [ drawDateCircle (monthDay timeZone group.match34.startTime) (circleOnCircle baseCircle (3 / 6) matchRadius) maskStyle ]

        -- Results
        -- result 12
        , drawCircle (circleOnCircle outerCircle 0 resultRadius) (filledByResult (matchResult group group.pot1.name group.pot2.name))

        -- result 13
        , drawCircle (circleOnCircle baseCircle -displacement resultRadius) (filledByResult (matchResult group group.pot1.name group.pot3.name))

        -- result 14
        , drawCircle (circleOnCircle baseCircle displacement resultRadius) (filledByResult (matchResult group group.pot1.name group.pot4.name))

        -- result 21
        , drawCircle (circleOnCircle innerCircle 0 resultRadius) (filledByResult (matchResult group group.pot2.name group.pot1.name))

        -- result 23
        , drawCircle (circleOnCircle innerCircle (2 / 3) resultRadius) (filledByResult (matchResult group group.pot2.name group.pot3.name))

        -- result 24
        , drawCircle (circleOnCircle innerCircle (1 / 3) resultRadius) (filledByResult (matchResult group group.pot2.name group.pot4.name))

        -- result 31
        , drawCircle (circleOnCircle baseCircle (2 / 3 + displacement) resultRadius) (filledByResult (matchResult group group.pot3.name group.pot1.name))

        -- result 32
        , drawCircle (circleOnCircle outerCircle (2 / 3) resultRadius) (filledByResult (matchResult group group.pot3.name group.pot2.name))

        -- result 34
        , drawCircle (circleOnCircle baseCircle (2 / 3 - displacement) resultRadius) (filledByResult (matchResult group group.pot3.name group.pot4.name))

        -- result 41
        , drawCircle (circleOnCircle baseCircle (1 / 3 - displacement) resultRadius) (filledByResult (matchResult group group.pot4.name group.pot1.name))

        -- result 42
        , drawCircle (circleOnCircle outerCircle (1 / 3) resultRadius) (filledByResult (matchResult group group.pot4.name group.pot2.name))

        -- result 43
        , drawCircle (circleOnCircle baseCircle (1 / 3 + displacement) resultRadius) (filledByResult (matchResult group group.pot4.name group.pot3.name))
        ]



-- MAIN


subscriptions : Model -> Sub Msg
subscriptions model =
    if List.length model.cardStack > 0 then
        Browser.Events.onKeyDown
            (Decode.field "key" Decode.string
                |> Decode.andThen
                    (\key ->
                        if key == "Escape" then
                            Decode.succeed CloseCard

                        else
                            Decode.fail "not escape"
                    )
            )

    else
        Sub.none


main : Program Flags Model Msg
main =
    Browser.element
        { init = init
        , update = update
        , view = view
        , subscriptions = subscriptions
        }
